import React, { useState, useEffect, useMemo, useRef } from "react";
import { Button, Select, notification, Space, Typography, Input, Divider, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { EditorState, convertFromRaw } from "draft-js";
import type { InputRef } from "antd";
import RichTextEditor from "@Components/shared/RichTextEditor";
import BaseModal from "@Components/shared/BaseModal";
import blocksConverter from "@Components/shared/RichTextEditor/blocksConverter";
import { useUpdateTerms, useCreateTerms, usePublishTerms } from "@Components/shared/mutations";
import { getTermVersionDisplayName, isTermPublished } from "@Utils";
import { ITerm } from "@Models/ITerms";

const { Option } = Select;
const { Text } = Typography;
interface EditTermsModalProps {
    visible: boolean;
    onClose: () => void;
    title: string;
    termsList: ITerm[];
    loading: boolean;
    onSave?: (termId: string, content: string) => void;
}

const EditTermsModal: React.FC<EditTermsModalProps> = ({ visible, onClose, title, termsList, loading, onSave }) => {
    const [selectedTermId, setSelectedTermId] = useState<string>("");
    const [selectedTermContent, setSelectedTermContent] = useState<string>("");
    const [selectedTermType, setSelectedTermType] = useState<string>("");
    const [editorState, setEditorState] = useState<EditorState | undefined>(undefined);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [newTermType, setNewTermType] = useState<string>("");
    const [isCreatingNewVersion, setIsCreatingNewVersion] = useState<boolean>(false);
    const [isInitialCreation, setIsInitialCreation] = useState<boolean>(false);
    const inputRef = useRef<InputRef>(null);

    // Use a ref to track the latest content to avoid state update timing issues
    const latestContentRef = useRef<string>("");

    // Use the updateTerms, createTerms, and publishTerms mutations
    const { mutate: updateTerms, isLoading: _isUpdating } = useUpdateTerms();
    const { mutate: createTerms, isLoading: isCreating } = useCreateTerms();
    const { mutate: publishTerms, isLoading: isPublishingMutation } = usePublishTerms();

    // Replace the memoized uniqueTermTypes with a state variable to allow adding custom types
    const [uniqueTermTypes, setUniqueTermTypes] = useState<string[]>([]);

    // Keep isSelectedTermPublished as a memoized value based on the selected term
    const isSelectedTermPublished = useMemo(() => {
        if (!selectedTermId) return false;

        const selectedTerm = termsList.find((term) => term.termsVersionId === selectedTermId);
        return isTermPublished(selectedTerm);
    }, [selectedTermId, termsList]);

    // Add a useEffect to initialize the uniqueTermTypes when termsList changes
    useEffect(() => {
        // Extract existing types from the termsList
        const typesFromTerms = new Set(termsList.map((term) => term.termsType));
        const typesArray = Array.from(typesFromTerms).filter(Boolean);

        // Update the state
        setUniqueTermTypes((prevTypes) => {
            // Merge previous custom types with types from termsList
            const allTypes = new Set([...typesArray, ...prevTypes]);
            return Array.from(allTypes);
        });
    }, [termsList]);

    // Reset state when modal is opened/closed
    useEffect(() => {
        if (!visible) {
            setSelectedTermId("");
            setSelectedTermContent("");
            latestContentRef.current = "";
            setSelectedTermType("");
            setEditorState(undefined);
            setIsSaving(false);
            setNewTermType("");
            setIsCreatingNewVersion(false);
            setIsInitialCreation(false);
        }
    }, [visible]);

    // Handle creating a new terms version
    const handleCreateNewVersion = () => {
        setIsCreatingNewVersion(true);
        setIsInitialCreation(true);
        setSelectedTermId("");
        setSelectedTermContent("");
        latestContentRef.current = "";
        setSelectedTermType("");
        setEditorState(undefined);
    };

    // Handle term selection from dropdown
    const handleTermSelection = (termId: string) => {
        setIsCreatingNewVersion(false);
        setSelectedTermId(termId);
        const selectedTerm = termsList.find((term) => term.termsVersionId === termId);

        if (selectedTerm) {
            setSelectedTermContent(selectedTerm.content);
            latestContentRef.current = selectedTerm.content;
            setSelectedTermType(selectedTerm.termsType || "");

            // Create an EditorState from the stored content
            try {
                // Try to parse the content as JSON (Draft.js raw format)
                const rawContent = JSON.parse(selectedTerm.content);

                // Use convertFromRaw to create a ContentState object
                const contentState = convertFromRaw(rawContent);

                // Create EditorState with the ContentState
                const newEditorState = EditorState.createWithContent(contentState);
                setEditorState(newEditorState);
            } catch (e) {
                // If not in correct format, create an empty editor state
                // and display the HTML content as a fallback
                setEditorState(EditorState.createEmpty());
                console.warn("Content not in expected Draft.js format:", e);
            }
        } else {
            // Clear editor state if no term is selected
            setEditorState(undefined);
            setSelectedTermContent("");
            latestContentRef.current = "";
            setSelectedTermType("");
        }
    };

    // Handle term type selection or creation
    const handleTermTypeChange = (value: string) => {
        // If the term is published, don't allow editing the type
        if (isSelectedTermPublished) {
            return;
        }

        setSelectedTermType(value);

        // If we're creating a new version, just update the state
        if (isCreatingNewVersion) {
            return;
        }

        // Update the terms with the new type
        if (selectedTermId) {
            setIsSaving(true);

            updateTerms(
                {
                    termsVersionId: selectedTermId,
                    termsType: value,
                    content: selectedTermContent,
                },
                {
                    onSuccess: (result) => {
                        setIsSaving(false);

                        // Only show success if the server didn't return an error
                        if (!result.hasErrors) {
                            notification.success({
                                message: "Terms type updated",
                                description: `Term type changed to "${value}".`,
                            });
                        } else {
                            notification.error({
                                message: "Failed to update terms type",
                                description: result.errors?.[0] || "There was an error updating the term type.",
                            });
                            console.error("Failed to update terms type:", result.errors);
                        }
                    },
                    onError: (error) => {
                        setIsSaving(false);
                        notification.error({
                            message: "Failed to update terms type",
                            description: "There was an error updating the term type.",
                        });
                        console.error("Failed to update terms type:", error);
                    },
                }
            );
        }
    };

    // Handle content changes from the rich text editor
    const handleEditorChange = (content: string) => {
        console.log("Editor content changed - length:", content.length);

        // If the term is published, don't allow editing
        if (isSelectedTermPublished) {
            return;
        }

        // Update both state and ref
        setSelectedTermContent(content);
        latestContentRef.current = content;

        // Skip auto-save for new versions - they'll be saved with the Create button
        if (isCreatingNewVersion) {
            console.log("In create mode, storing content for later use with Create button");
            return;
        }

        // Only proceed if we have a term ID for updating
        if (!selectedTermId) {
            return;
        }

        setIsSaving(true);

        // Update existing terms
        updateTerms(
            {
                termsVersionId: selectedTermId,
                termsType: selectedTermType,
                content: content,
            },
            {
                onSuccess: (result) => {
                    setIsSaving(false);

                    // Only show success if the server didn't return an error
                    if (!result.hasErrors) {
                        notification.success({
                            message: "Terms updated",
                            description: "Your changes have been saved automatically.",
                        });
                    } else {
                        notification.error({
                            message: "Failed to update terms",
                            description: result.errors?.[0] || "There was an error saving your changes.",
                        });
                        console.error("Failed to update terms:", result.errors);
                    }
                },
                onError: (error) => {
                    setIsSaving(false);
                    notification.error({
                        message: "Failed to update terms",
                        description: "There was an error saving your changes.",
                    });
                    console.error("Failed to update terms:", error);
                },
            }
        );
    };

    // Handle creating a new terms version
    const handleCreateTerms = () => {
        // Validate that we have a type selected
        if (!selectedTermType) {
            notification.warning({
                message: "Term type required",
                description: "Please select a term type before creating a new terms version.",
            });
            return;
        }

        setIsSaving(true);

        // Create with empty content initially
        createTerms(
            {
                termsType: selectedTermType,
                content: "",
            },
            {
                onSuccess: (result) => {
                    setIsSaving(false);

                    if (result && result.value) {
                        const newTermId = result.value.termsVersionId || "";
                        console.log("Successfully created new term with ID:", newTermId);

                        // Update state to show editor
                        setIsInitialCreation(false);
                        setIsCreatingNewVersion(false);
                        setSelectedTermId(newTermId);
                        setEditorState(EditorState.createEmpty());

                        notification.success({
                            message: "New terms version created",
                            description: "You can now edit the terms content.",
                        });
                    } else {
                        notification.error({
                            message: "Failed to create terms",
                            description: "Terms were created but the response was incomplete.",
                        });
                    }
                },
                onError: (error) => {
                    setIsSaving(false);
                    notification.error({
                        message: "Failed to create terms",
                        description: error instanceof Error ? error.message : "There was an error creating the terms.",
                    });
                    console.error("Failed to create terms:", error);
                },
            }
        );
    };

    // Handle new term type input change
    const onNewTermTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewTermType(event.target.value);
    };

    // Update the addTermType function to not use setTimeout
    const addTermType = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        console.log("Adding new term type:", newTermType);

        if (!newTermType.trim()) {
            notification.warning({
                message: "Empty term type",
                description: "Please enter a valid term type name.",
            });
            return;
        }

        // Get the trimmed type
        const trimmedType = newTermType.trim();
        console.log("Setting term type to:", trimmedType);

        // Update selected term type directly
        setSelectedTermType(trimmedType);

        // Add to uniqueTermTypes if not already there
        setUniqueTermTypes((prevTypes) => {
            if (!prevTypes.includes(trimmedType)) {
                console.log("Adding new type to list:", trimmedType);
                return [...prevTypes, trimmedType];
            }
            return prevTypes;
        });

        // Clear the input and show success
        setNewTermType("");
        notification.success({
            message: "Term type added",
            description: `Added "${trimmedType}" as a new term type.`,
        });
    };

    // Add an effect to focus the input after newTermType is cleared
    useEffect(() => {
        if (newTermType === "" && inputRef.current) {
            inputRef.current.focus();
        }
    }, [newTermType]);

    // Unused but kept for future implementation
    const _handleSave = () => {
        if (onSave && selectedTermId) {
            onSave(selectedTermId, selectedTermContent);
        }
        onClose();
    };

    // Handle publish terms without using setTimeout
    const handlePublishTerms = () => {
        if (!selectedTermId) return;

        publishTerms(selectedTermId, {
            onSuccess: (result) => {
                if (!result?.hasErrors) {
                    notification.success({
                        message: "Terms published successfully",
                        description: "The terms version has been published and is now available to users.",
                    });
                } else {
                    notification.error({
                        message: "Failed to publish terms",
                        description: result?.errors?.[0] || "There was an error publishing the terms.",
                    });
                }
            },
            onError: (error) => {
                notification.error({
                    message: "Failed to publish terms",
                    description: error instanceof Error ? error.message : "There was an error publishing the terms.",
                });
                console.error("Failed to publish terms:", error);
            },
        });
    };

    // Add visual styling to the editor when it's disabled
    const editorDisabledStyle = isSelectedTermPublished
        ? {
              opacity: 0.7,
              pointerEvents: "none" as const,
              backgroundColor: "#f5f5f5",
          }
        : {};

    // In the return statement, add modalContent declaration before it's used
    const modalContent = (
        <>
            <div style={{ marginBottom: "20px" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                    <div>
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-end",
                                marginBottom: "8px",
                            }}
                        >
                            <div style={{ flex: 1, marginRight: "16px" }}>
                                <label style={{ display: "block", marginBottom: "8px" }}>Select Terms Version:</label>
                                <Select
                                    style={{ width: "100%" }}
                                    value={selectedTermId || undefined}
                                    onChange={handleTermSelection}
                                    placeholder="Select a terms version"
                                    loading={loading}
                                    disabled={isCreatingNewVersion}
                                >
                                    {termsList.map((term) => (
                                        <Option key={term.termsVersionId} value={term.termsVersionId}>
                                            {getTermVersionDisplayName(term, termsList)}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                            <Tooltip title="Create new terms version">
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleCreateNewVersion}
                                    disabled={isCreatingNewVersion}
                                >
                                    New Version
                                </Button>
                            </Tooltip>
                        </div>
                    </div>

                    {(selectedTermId || isCreatingNewVersion) && (
                        <div>
                            <label style={{ display: "block", marginBottom: "8px" }}>
                                Terms Type:
                                {isCreatingNewVersion && (
                                    <Text type="secondary" style={{ marginLeft: "8px" }}>
                                        (required for creation)
                                    </Text>
                                )}
                            </label>
                            <Select
                                style={{ width: "100%" }}
                                value={selectedTermType}
                                onChange={handleTermTypeChange}
                                placeholder="Select or create a terms type"
                                showSearch
                                allowClear
                                disabled={isSelectedTermPublished}
                                status={isCreatingNewVersion && !selectedTermType ? "error" : undefined}
                                dropdownRender={(menu) => (
                                    <>
                                        {menu}
                                        <Divider style={{ margin: "8px 0" }} />
                                        <Space style={{ padding: "0 8px 4px" }}>
                                            <Input
                                                placeholder="Enter new term type"
                                                ref={inputRef}
                                                value={newTermType}
                                                onChange={onNewTermTypeChange}
                                                disabled={isSelectedTermPublished}
                                                onPressEnter={(e) => {
                                                    e.preventDefault();
                                                    addTermType(e as unknown as React.MouseEvent<HTMLButtonElement>);
                                                }}
                                            />
                                            <Button
                                                type="text"
                                                icon={<PlusOutlined />}
                                                onClick={addTermType}
                                                disabled={!newTermType.trim() || isSelectedTermPublished}
                                            >
                                                Add
                                            </Button>
                                        </Space>
                                    </>
                                )}
                                options={uniqueTermTypes.map((type) => ({ label: type, value: type }))}
                            />
                            <Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: "10px" }}>
                                {isCreatingNewVersion && !selectedTermType ? (
                                    <Text type="danger">Please select a term type and click Create to save</Text>
                                ) : (
                                    "Select from existing types or create a new one"
                                )}
                            </Text>
                        </div>
                    )}
                </Space>
            </div>

            {isSaving && (
                <div style={{ marginBottom: "10px", color: "#1890ff" }}>
                    {isCreatingNewVersion ? "Creating new terms version..." : "Saving changes..."}
                </div>
            )}

            {selectedTermId || (isCreatingNewVersion && !isInitialCreation) ? (
                <>
                    {isSelectedTermPublished && (
                        <div
                            style={{
                                marginBottom: "10px",
                                backgroundColor: "#f5f5f5",
                                padding: "8px 12px",
                                borderRadius: "4px",
                                color: "#666",
                            }}
                        >
                            <strong>Read-only:</strong> This terms version is published and cannot be edited.
                        </div>
                    )}
                    <div style={editorDisabledStyle}>
                        <RichTextEditor
                            key={isCreatingNewVersion ? "new-version" : selectedTermId}
                            placeholder={
                                isCreatingNewVersion
                                    ? "Enter new terms content..."
                                    : "Content will appear here when a terms version is selected..."
                            }
                            initialContent={editorState}
                            onChange={(content) => {
                                console.log("change");
                                if (!isSelectedTermPublished) {
                                    console.log("Editor onChange called with content size:", content.length);
                                    console.log("Content preview:", content.substring(0, 100));
                                    handleEditorChange(content);
                                }
                            }}
                        />
                    </div>
                </>
            ) : (
                <div className="rich-text-editor-root">
                    <div className="rich-text-editor-editor" style={{ padding: "15px", minHeight: "200px" }}>
                        <p style={{ color: "#aaa" }}>
                            {loading
                                ? "Loading terms versions..."
                                : isInitialCreation
                                ? "Please select a term type and click Create to continue."
                                : "Please select a terms version or create a new one to edit."}
                        </p>
                    </div>
                </div>
            )}

            {selectedTermContent && !editorState && !isCreatingNewVersion && (
                <div style={{ marginTop: "20px", padding: "15px", border: "1px solid #ddd" }}>
                    {blocksConverter({ data: selectedTermContent })}
                </div>
            )}
        </>
    );

    const footer = (
        <>
            {selectedTermId &&
                !isCreatingNewVersion &&
                (isSelectedTermPublished ? (
                    <Button disabled type="default">
                        Published
                    </Button>
                ) : (
                    <Button
                        type="primary"
                        onClick={handlePublishTerms}
                        loading={isPublishingMutation}
                        disabled={!selectedTermId || isPublishingMutation || isSaving}
                    >
                        Publish
                    </Button>
                ))}

            {isCreatingNewVersion && (
                <Button
                    key="create"
                    type="primary"
                    onClick={handleCreateTerms}
                    loading={isSaving}
                    disabled={!selectedTermType || isCreating}
                >
                    Create
                </Button>
            )}
        </>
    );

    return (
        <BaseModal
            visible={visible}
            onClose={onClose}
            title={title}
            footer={footer}
            width={900}
            bodyStyle={{ maxHeight: "60vh", overflowY: "auto", paddingLeft: "70px", paddingRight: "70px" }}
        >
            {modalContent}
        </BaseModal>
    );
};

export default EditTermsModal;
